import { DatePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	ButtonIconComponent,
	Icons,
	IconComponent,
	IMenu,
	InfoGroupComponent,
	ItemComponent,
	ListDetailComponent,
	MenuComponent,
	TagComponent,
} from '@tecalisdev/infinity';
import { MenuItemAction } from '../../../shared/models/master.models';
import { MasterService } from '../../../shared/services/master.service';
import { IOperationDetailResponse, OPERATION_TYPE } from '../../models/operations.models';
import { resolveOperationDetailEffectiveState } from '../../utils/form-operation-match-data.util';
import { OperationsHelperService } from '../../services/operations-helper.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { UuidFlexibleDisplayComponent } from '../../../shared/components/uuid-flexible-display/uuid-flexible-display.component';
import { OperationDetailItemExtraInfoComponent } from './operation-detail-item-extra-info/operation-detail-item-extra-info.component';

@Component({
	selector: 'app-operation-detail-item',
	imports: [
		TranslatePipe,
		DatePipe,
		ItemComponent,
		ListDetailComponent,
		TagComponent,
		InfoGroupComponent,
		IconComponent,
		MenuComponent,
		ButtonIconComponent,
		UuidFlexibleDisplayComponent,
		OperationDetailItemExtraInfoComponent,
	],
	templateUrl: './operation-detail-item.component.html',
	styleUrl: './operation-detail-item.component.scss',
})
export class OperationDetailItemComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;
	/** Acciones del menú que delegan en el padre (modales / API). */
	@Output() headerMenuAction = new EventEmitter<MenuItemAction>();

	private navigationService = inject(NavigationService);
	public operationsHelper = inject(OperationsHelperService);
	public masterService = inject(MasterService);

	public OPERATION_TYPE = OPERATION_TYPE;
	public operationTypeIcon: Icons = 'icon-user';

	public get displayState(): string | undefined {
		return resolveOperationDetailEffectiveState(this.detail);
	}

	/** Estado usable para tag (ignora vacío / "NULL"). */
	public get effectiveState(): string | undefined {
		const raw = this.displayState ?? this.detail?.state_name;
		if (raw == null) {
			return undefined;
		}
		const normalized = String(raw).trim().toUpperCase();
		if (!normalized || normalized === 'NULL' || normalized === 'UNDEFINED') {
			return undefined;
		}
		return normalized;
	}

	public get statusTagLabelKey(): string | null {
		const state = this.effectiveState;
		return state ? `MASTER.STATUS.${state}` : null;
	}

	private operationTypeIcons: Record<string, Icons> = {
		[OPERATION_TYPE.OCR]: 'icon-card',
		[OPERATION_TYPE.POL]: 'icon-user',
		[OPERATION_TYPE.DOCUMENTS]: 'icon-file',
		[OPERATION_TYPE.CONNECTIONS]: 'icon-euro',
		[OPERATION_TYPE.FINANCIAL_EVALUATION]: 'icon-euro',
		[OPERATION_TYPE.EVALUATION]: 'icon-euro',
		[OPERATION_TYPE.ATTORNEY_POWERS]: 'icon-shield-check',
		[OPERATION_TYPE.BORME]: 'icon-file',
		[OPERATION_TYPE.FORM]: 'icon-fill-form',
		[OPERATION_TYPE.TFA]: 'icon-message-circle',
		'2FA': 'icon-message-circle',
		[OPERATION_TYPE.SMS]: 'icon-message-circle',
		[OPERATION_TYPE.WHATSAPP]: 'icon-message-circle',
	};

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.updateIcon();
	}

	private updateIcon(): void {
		const operationType = this.detail?.operation_type_name?.toUpperCase();
		if (!operationType) {
			this.operationTypeIcon = 'icon-user';
			return;
		}

		const dataType = String(this.detail?.data?.['type'] ?? '').toUpperCase();
		if (
			dataType === 'SCRIPTURE' &&
			(operationType === OPERATION_TYPE.CONNECTIONS ||
				operationType === OPERATION_TYPE.EVALUATION ||
				operationType === OPERATION_TYPE.FINANCIAL_EVALUATION)
		) {
			this.operationTypeIcon = 'icon-shield-check';
			return;
		}

		this.operationTypeIcon = this.operationTypeIcons[operationType] ?? 'icon-user';
	}

	public itemMenuChange(action: IMenu): void {
		const id = action.id as MenuItemAction;
		if (id === MenuItemAction.STATUS || id === MenuItemAction.BLOCK || id === MenuItemAction.UNLOCK) {
			this.headerMenuAction.emit(id);
			return;
		}
		const actions: Partial<Record<MenuItemAction, () => void>> = {
			[MenuItemAction.SHOW_PROCESS]: () => this.goToProcess(),
			[MenuItemAction.SHOW_IDENTITY]: () => this.goToIdentity(),
			[MenuItemAction.SHOW_LOGS]: () => {
				if (this.detail?.logs_url) {
					window.open(this.detail.logs_url, '_blank', 'noopener,noreferrer');
				}
			},
		};
		actions[id]?.();
	}

	private goToProcess(event?: Event): void {
		if (!this.detail?.process_uuid) return;
		this.navigationService.navigateTo({ routeSegments: ['processes', 'detail', this.detail.process_uuid] }, event);
	}

	private goToIdentity(event?: Event): void {
		if (!this.detail?.identity?.uuid) return;
		this.navigationService.navigateTo(
			{ routeSegments: ['identities', 'detail', this.detail.identity.uuid] },
			event,
		);
	}
}
